import { useState } from 'react';
import { EvaluatorLink } from 'api';
import DirectLinkCard from 'components/DirectLinkCard/DirectLinkCard';
import { Box, styled, Tab, Tabs } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import {
  TextV2,
  SortSelect,
  Counter,
} from '@insights-ltd/design-library/components';
import { DIALECT_MAP } from 'variables';
import { Dialects } from 'types/dialects';
import { EvaluatorLinksSortCategory } from 'types/types';
import { getMinimumSearchCharacters, SearchMode } from 'api/search';
import {
  ApiPaginationProps,
  Pagination,
} from 'components/Pagination/Pagination';
import {
  DEFAULT_CARD_ITEMS_PER_PAGE,
  Paginator,
} from 'components/hooks/usePaginationV2';
import { FullScreenSpinner } from 'components/FullScreen';
import { EvaluatorLinkListSearchFilter } from '../EvaluatorLinkListSearchFilter';

export const ITEMS_PER_PAGE = 9;

export type EvaluatorLinkFilter = 'ALL' | 'PRACTITIONER';

type EvaluatorLinkListProps = {
  searchMode: SearchMode;
  onSearchModeChange: (mode: SearchMode) => void;
  isOrgSpecific: boolean;
  paginator: Paginator<EvaluatorLink>;
  filter: EvaluatorLinkFilter;
  searchTerm?: string;
  onFilterChange: (value: EvaluatorLinkFilter) => void;
  onSearchTermChange: (value: string) => void;
  sortCategory: EvaluatorLinksSortCategory;
  setLinkSortBy: (sortBy: EvaluatorLinksSortCategory) => void;
  isSearchTooShort: boolean;
  apiPaginationProps?: ApiPaginationProps;
  showResultsLoadingState?: boolean;
};

const translations = {
  noneCreated: 'ui.event-management.evaluator-links.none-created',
  sortOptions: {
    dateCreated:
      'ui.event-management.evaluator-links.direct-link-card.sort-by-date-created',
    mostCompletedEvaluators:
      'ui.event-management.evaluator-links.direct-link-card.sort-by-most-completed-evaluators',
    leastCompletedEvaluators:
      'ui.event-management.evaluator-links.direct-link-card.sort-by-least-completed-evaluators',
  },
  noResults: 'ui.event-management.evaluator-links.no-results',
};

const StyledP = styled(TextV2)(({ theme }) => ({
  marginTop: `${theme.spacing(spacingSizeMap.S)} !important`,
  marginLeft: `${theme.spacing(spacingSizeMap.S)} !important`,
  fontWeight: theme.typography.fontWeightBold,
}));

const LinksContainer = ({
  linkCards,
  searchTitle,
  totalCount,
  paginator,
  apiPaginationProps,
  sortCategory,
  setLinkSortBy,
}: {
  paginator: Paginator<EvaluatorLink>;
  apiPaginationProps?: ApiPaginationProps;
  linkCards: JSX.Element[];
  searchTitle: string;
  totalCount: number;
  sortCategory: EvaluatorLinksSortCategory;
  setLinkSortBy: (sortBy: EvaluatorLinksSortCategory) => void;
}) => {
  enum SortType {
    Date = 'DATE_CREATED',
    MostCompleted = 'MOST_COMPLETED',
    LeastCompleted = 'LEAST_COMPLETED',
  }

  const { t } = useTranslation();

  const onSortByChange = (e: {
    target: { value: React.SetStateAction<string> };
  }) => {
    setLinkSortBy(e?.target?.value as EvaluatorLinksSortCategory);
  };

  const sortOptions: { label: string; value: SortType }[] = [
    { label: t(translations.sortOptions.dateCreated), value: SortType.Date },
    {
      label: t(translations.sortOptions.mostCompletedEvaluators),
      value: SortType.MostCompleted,
    },
    {
      label: t(translations.sortOptions.leastCompletedEvaluators),
      value: SortType.LeastCompleted,
    },
  ];

  if (totalCount === 0) {
    return (
      <>
        <Box
          sx={(theme) => ({
            marginTop: `${theme.spacing(spacingSizeMap.M)} !important`,
            marginLeft: `${theme.spacing(spacingSizeMap.XS)} !important`,
            marginBottom: `${theme.spacing(spacingSizeMap.XS)} !important`,
          })}
        >
          <TextV2 component="h2" variant="h3">
            {searchTitle}
            <Counter
              count={totalCount}
              size="large"
              sx={{
                marginLeft: 1,
              }}
              dataTestId="count-box"
            />
          </TextV2>
        </Box>
        <StyledP color="textSecondary" variant="bodyBold">
          {t(translations.noResults)}
        </StyledP>
      </>
    );
  }
  return (
    <>
      <Box
        sx={{
          marginBottom: '2px',
        }}
      >
        <TextV2 component="h2" variant="h3">
          {searchTitle}
          <Counter
            count={totalCount}
            size="large"
            sx={{
              marginLeft: 1,
            }}
            dataTestId="count-box"
          />
        </TextV2>
      </Box>
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: 0,
        }}
        size={{ xs: 12, sm: 'auto' }}
      >
        <Grid
          container
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            px: 0,
            width: '100%',
          }}
        >
          <Pagination
            itemsPerPage={ITEMS_PER_PAGE}
            paginator={paginator}
            apiPaginationProps={apiPaginationProps}
          />
          <Box sx={{ ml: 'auto' }}>
            <SortSelect
              labelText={t(
                'ui.event-management.evaluator-links.direct-link-card.sort-by',
              )}
              sortOptions={sortOptions}
              onChange={onSortByChange}
              value={sortCategory}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        {linkCards}
      </Grid>
      <Grid
        container
        direction="row"
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          padding: `${theme.spacing(spacingSizeMap.S)} 0`,
        })}
      >
        <Pagination
          itemsPerPage={DEFAULT_CARD_ITEMS_PER_PAGE}
          paginator={paginator}
          apiPaginationProps={apiPaginationProps}
        />
      </Grid>
    </>
  );
};

const EvaluatorLinkList = ({
  isOrgSpecific,
  paginator,
  apiPaginationProps,
  filter,
  searchTerm,
  onFilterChange,
  onSearchTermChange,
  sortCategory,
  setLinkSortBy,
  searchMode,
  onSearchModeChange,
  isSearchTooShort,
  showResultsLoadingState,
}: EvaluatorLinkListProps) => {
  const { t } = useTranslation();
  const { pagedItems: pagedLinks, totalCount } = paginator;
  const [lastCopiedLinkId, setLastCopiedLinkId] = useState<string>();
  const minSearchCharacterLimit = getMinimumSearchCharacters(searchMode);

  const getDialectTranslationKey = (dialect: Dialects) =>
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    t(DIALECT_MAP.find(({ value }) => value === dialect)!.textKey);

  const directLinks = pagedLinks.map(
    (
      {
        id,
        link,
        name,
        organisation,
        totalCompletedEvaluators,
        evaluatorModel,
        dialect: code = 'en-GB',
      },
      index,
    ) => (
      <Grid
        key={id}
        sx={{
          paddingTop: index > 2 ? '1rem !important' : '0 !important',
          paddingLeft: '17px !important',
        }}
        size={{ xs: 12, sm: 6, md: 4 }}
      >
        <DirectLinkCard
          id={id}
          evaluatorLink={link}
          organisation={organisation}
          name={name}
          completedEvaluators={totalCompletedEvaluators}
          linkCopied={lastCopiedLinkId === id}
          onCopy={setLastCopiedLinkId}
          evaluatorModel={evaluatorModel}
          dialect={{ code, name: getDialectTranslationKey(code) }}
        />
      </Grid>
    ),
  );

  const searchTranslations = {
    PRACTITIONER: {
      org: {
        noSearchTerm:
          'ui.event-management.evaluator-links.list.my-evaluator-links',
        searchTerm:
          'ui.event-management.evaluator-links.list-header.search-term',
      },
      all: {
        noSearchTerm:
          'ui.event-management.evaluator-links.list-header.all.my-evaluator-links',
        searchTerm:
          'ui.event-management.evaluator-links.list-header.all.search-term',
      },
    },
    ALL: {
      org: {
        noSearchTerm: 'ui.event-management.evaluator-links.list-header',
        searchTerm:
          'ui.event-management.evaluator-links.list-header.search-term',
      },
      all: {
        noSearchTerm: 'ui.event-management.evaluator-links.list-header.all',
        searchTerm:
          'ui.event-management.evaluator-links.list-header.all.search-term',
      },
    },
  };

  const {
    searchTerm: searchTermTranslation,
    noSearchTerm: noSearchTermTranslation,
  } = searchTranslations[filter][isOrgSpecific ? 'org' : 'all'];

  const searchResultsTitle =
    searchTerm && searchTerm.length >= minSearchCharacterLimit
      ? t(searchTermTranslation, { searchTerm })
      : t(noSearchTermTranslation);

  const renderLinks = () => {
    if (isSearchTooShort) {
      return null;
    }
    if (showResultsLoadingState) {
      return (
        <FullScreenSpinner message={t('ui.event-management.loading')} inline />
      );
    }
    const hasValidSearch =
      !!searchTerm && searchTerm.length >= minSearchCharacterLimit;

    if (totalCount > 0 || hasValidSearch) {
      return (
        <LinksContainer
          linkCards={directLinks}
          paginator={paginator}
          apiPaginationProps={apiPaginationProps}
          totalCount={totalCount}
          searchTitle={searchResultsTitle}
          sortCategory={sortCategory}
          setLinkSortBy={setLinkSortBy}
        />
      );
    }

    return (
      <Box>
        <StyledP color="textSecondary" variant="bodyBold">
          {t(translations.noneCreated)}
        </StyledP>
      </Box>
    );
  };

  return (
    <Grid>
      <Box
        sx={(theme) => ({
          marginBottom: theme.spacing(spacingSizeMap.S),
        })}
      >
        <Tabs
          value={filter}
          indicatorColor="primary"
          textColor="primary"
          onChange={(_e, value) => {
            onFilterChange(value);
          }}
        >
          <Tab
            sx={(theme) => ({
              color:
                filter === 'ALL'
                  ? `${theme.palette.text.primary} !important`
                  : theme.palette.blue.main,
              minWidth: '4.5rem',
              margin: '0 0.5rem',
              padding: '16px 8px',
              fontWeight: theme.typography.fontWeightBold,
            })}
            value="ALL"
            label={t('ui.event-management.evaluator-links.filter.all-links')}
          />
          <Tab
            sx={(theme) => ({
              color:
                filter === 'PRACTITIONER'
                  ? `${theme.palette.text.primary} !important`
                  : theme.palette.blue.main,
              minWidth: '4.5rem',
              margin: '0 0.5rem',
              padding: '16px 8px',
              fontWeight: theme.typography.fontWeightBold,
            })}
            value="PRACTITIONER"
            label={t(
              'ui.event-management.evaluator-links.filter.my-created-links',
            )}
          />
        </Tabs>
      </Box>

      <EvaluatorLinkListSearchFilter
        {...{ searchTerm, onSearchTermChange, searchMode, onSearchModeChange }}
      />

      {renderLinks()}
    </Grid>
  );
};

export default EvaluatorLinkList;
